import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  Activity as ActivityIcon,
  Droplet,
  Plug,
  Power,
  Wifi,
} from "lucide-react";
import { api, type Device } from "~/lib/api";
import { useActivityEvents, type ActivityEvent } from "~/lib/live";
import { useRelativeTime } from "~/lib/time";
import { AppShell } from "~/components/AppShell";
import { Group } from "~/components/ui/Group";
import { RowSeparator } from "~/components/ui/Row";
import { IconBadge, type IconBadgeTint } from "~/components/ui/IconBadge";

export const Route = createFileRoute("/_authed/activity")({
  component: ActivityPage,
});

function ActivityPage() {
  const events = useActivityEvents();
  const { data: devices } = useQuery({
    queryKey: ["devices"],
    queryFn: () => api.listDevices(),
  });

  const deviceById = useMemo(() => {
    const m = new Map<string, Device>();
    for (const d of devices ?? []) m.set(d.device_id, d);
    return m;
  }, [devices]);

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto pt-4 sm:pt-6 lg:pt-8">
        <header className="pb-6">
          <h1 className="text-largeTitle" style={{ color: "var(--label-primary)" }}>
            Activity
          </h1>
          <div className="text-subhead mt-1" style={{ color: "var(--label-secondary)" }}>
            Recent events from your devices.
          </div>
        </header>

        {events.length === 0 ? (
          <EmptyActivity />
        ) : (
          <Group>
            {events.map((e, i) => (
              <div key={e.id}>
                <EventRow event={e} device={deviceById.get(e.deviceId)} />
                {i < events.length - 1 && <RowSeparator inset={56} />}
              </div>
            ))}
          </Group>
        )}
      </div>
    </AppShell>
  );
}

function EventRow({ event, device }: { event: ActivityEvent; device: Device | undefined }) {
  const when = useRelativeTime(event.at);
  const deviceName = device?.name?.trim() || prettyId(event.deviceId);
  const isAquagate = device?.device_type === "aquagate";

  const view = describeEvent(event, isAquagate);

  return (
    <Link
      to="/$deviceId"
      params={{ deviceId: event.deviceId }}
      className="flex items-center gap-3 px-4 py-3 transition-colors active:bg-[var(--fill-quaternary)]"
    >
      <IconBadge tint={view.tint} size={28}>
        {view.icon}
      </IconBadge>
      <div className="flex-1 min-w-0">
        <div className="text-body truncate" style={{ color: "var(--label-primary)" }}>
          {view.title}
        </div>
        <div className="text-footnote truncate mt-0.5" style={{ color: "var(--label-secondary)" }}>
          {deviceName}
        </div>
      </div>
      <span
        className="text-footnote shrink-0 tabular-nums"
        style={{ color: "var(--label-tertiary)" }}
      >
        {when}
      </span>
    </Link>
  );
}

function describeEvent(
  event: ActivityEvent,
  isAquagate: boolean,
): { title: string; icon: React.ReactNode; tint: IconBadgeTint } {
  switch (event.kind) {
    case "actuator": {
      const label = aquagatePinLabel(isAquagate, event.pin);
      const action = event.on ? "turned on" : "turned off";
      const tint: IconBadgeTint = event.on ? "green" : "gray";
      const icon =
        event.pin <= 1 ? <Droplet size={15} /> : event.pin === 2 ? <Plug size={15} /> : <Power size={15} />;
      return { title: `${label} ${action}`, icon, tint };
    }
    case "online":
      return {
        title: "Came online",
        icon: <Wifi size={15} />,
        tint: "green",
      };
    case "offline":
      return {
        title: "Went offline",
        icon: <Wifi size={15} />,
        tint: "gray",
      };
  }
}

function aquagatePinLabel(isAquagate: boolean, pin: number): string {
  if (isAquagate) {
    if (pin === 0) return "Valve A";
    if (pin === 1) return "Valve B";
    if (pin === 2) return "AC switch";
  }
  return `Output ${pin + 1}`;
}

function EmptyActivity() {
  return (
    <div className="flex-1 flex items-center justify-center min-h-[40vh] py-12">
      <div className="text-center max-w-sm px-4">
        <span
          aria-hidden
          className="grid place-items-center mx-auto mb-5"
          style={{
            width: 64,
            height: 64,
            borderRadius: 18,
            background: "var(--fill-tertiary)",
            color: "var(--label-secondary)",
          }}
        >
          <ActivityIcon size={28} />
        </span>
        <div className="text-title-3" style={{ color: "var(--label-primary)" }}>
          No activity yet
        </div>
        <p className="text-body mt-2" style={{ color: "var(--label-secondary)" }}>
          Toggle a valve, or wait for a device to come online — events will appear here as they happen.
        </p>
      </div>
    </div>
  );
}

function prettyId(deviceId: string) {
  if (!deviceId) return "Device";
  return deviceId.length > 10 ? `Device ${deviceId.slice(-6).toUpperCase()}` : deviceId;
}
